import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, ShieldCheck, Zap, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePump } from "@/context/PumpContext";
import { formatError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { BigLogo } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const { pump, setPump, locked, resolving } = usePump();
  const nav = useNavigate();
  const [empId, setEmpId] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!pump && !resolving) nav("/", { replace: true }); }, [pump, resolving, nav]);
  if (!pump) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  const outlet = BRAND.outlet;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(empId.trim(), pw);
      toast.success(`Welcome, ${u.name}`);
      nav(["admin", "manager"].includes(u.role) ? "/admin" : "/selfie");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-white">
      {/* Hero side */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={BRAND.pumpHero}
          alt="Bharti Energy Point outlet at night"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Blue overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#001b3a]/85 via-[#003a72]/70 to-[#00559e]/40" />
        {/* Grain pattern */}
        <div className="absolute inset-0 opacity-20 pattern-dots" />

        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <BigLogo className="w-14 h-14 rounded-2xl bg-white/95 shadow-md p-1" />
            <div>
              <div className="font-display text-lg font-bold tracking-tight" data-testid="brand-name">{pump.name}</div>
              <div className="text-xs text-white/85">{outlet}</div>
            </div>
          </div>

          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm px-3 py-1 text-xs font-medium">
              <Zap className="w-3.5 h-3.5" /> Dam Duty · Live Shift Console
            </div>
            <h1 className="mt-4 text-5xl font-display font-bold leading-[1.05] tracking-tight">
              Every litre.<br />Every shift.<br />
              <span className="text-[color:var(--bpcl-orange)]">Perfectly tracked.</span>
            </h1>
            <p className="mt-4 text-white/90 max-w-md">{BRAND.tagline}</p>

            <div className="mt-8 grid grid-cols-3 gap-3">
              <FeatureChip icon={Droplets} label="Meter readings" />
              <FeatureChip icon={ShieldCheck} label="Chain of custody" />
              <FeatureChip icon={Zap} label="Real-time handover" />
            </div>
            <div className="mt-8 h-1 w-16 bg-[color:var(--bpcl-orange)] rounded-full" />
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50 relative">
        <div
          className="absolute inset-0 opacity-40 lg:hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url(${BRAND.pumpSide})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Card className="relative w-full max-w-md p-6 sm:p-9 rounded-2xl shadow-lg border-slate-200 bg-white/95 backdrop-blur">
          <div className="mb-6 lg:hidden flex items-center gap-3">
            <BigLogo className="w-14 h-14 rounded-2xl bg-white ring-1 ring-slate-200 p-1" />
            <div>
              <div className="font-display font-bold text-lg">{pump.name}</div>
              <div className="text-xs text-slate-500">{outlet}</div>
            </div>
          </div>
          <div className="hidden lg:flex items-center gap-3 mb-4">
            <BigLogo className="w-14 h-14 rounded-xl bg-white ring-1 ring-slate-200 p-1" />
            <div>
              <div className="font-display font-bold text-slate-900">Welcome back</div>
              <div className="text-xs text-slate-500">Sign in to your shift console</div>
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl font-display font-bold mb-1">Sign in</h2>
          <p className="text-sm text-slate-500 mb-6">
            Enter your Employee ID to start your shift.
            {!locked && <button type="button" onClick={() => { setPump(null); nav("/select-pump"); }} className="ml-2 text-[color:var(--bpcl-blue)] hover:underline" data-testid="change-pump-btn">Not {pump.name}? Change pump</button>}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="emp">Employee ID</Label>
              <Input
                id="emp"
                data-testid="login-employee-id"
                autoFocus
                autoComplete="username"
                value={empId}
                onChange={(e) => setEmpId(e.target.value)}
                className="h-12 rounded-xl text-base"
                placeholder="Employee ID"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw">Password</Label>
              <Input
                id="pw"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="h-12 rounded-xl text-base"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)] text-white text-base font-semibold shadow-md hover:scale-[1.01] active:scale-[0.99] transition-transform"
            >
              {loading ? "Signing in…" : (<><LogIn className="w-4 h-4 mr-2" /> LOGIN</>)}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

function FeatureChip({ icon: Icon, label }) {
  return (
    <div className="rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm p-2.5 flex items-center gap-2 text-sm font-medium">
      <Icon className="w-4 h-4 text-[color:var(--bpcl-orange)]" />
      <span className="truncate">{label}</span>
    </div>
  );
}
